import PropTypes from 'prop-types';
import React, { PureComponent, Component } from 'react';

import Sizer from './Sizer';
import ScrollPane from './ScrollPane';
import Positioner from './Positioner';
import Artboard from './Artboard';
import { ConnectedInputHandler } from './InputHandler';
import { SketchLayer, Path, ProvidePaths } from './SketchLayer';
import { ConnectedAnnotationsCanvas } from '../AnnotationsCanvas';
import AnnotationTooltip from '../AnnotationTooltip';
import { FormattedMessage } from 'react-intl';
import Desktop from '@99designs/react-components/components/Desktop';
import Mobile from '@99designs/react-components/components/Mobile';
import MediaQuery from 'react-responsive';
import breakpoints from '@99designs/asimov/platform-settings/settings/breakpoints';
import ZoomLevels from '../../constants/zoom-level-constants';

export default class DesignPane extends Component {
    render() {
        const imageSize = {
            width: this.props.design.width,
            height: this.props.design.height,
        };

        return (
            <MediaQuery maxWidth={breakpoints.normal[0]}>
                {isMobile => (
                    <div style={{ minHeight: isMobile ? '100vw' : undefined }}>
                        <Sizer
                            onSize={this.props.onSize}
                            render={viewSize => {
                                let scale;
                                if (
                                    isMobile ||
                                    this.props.zoomLevel == ZoomLevels.FLUID ||
                                    this.props.zoomLevel == ZoomLevels.FIT_TO_WIDTH
                                ) {
                                    scale = viewSize.width / imageSize.width;
                                } else if (this.props.zoomLevel == ZoomLevels.FIT_TO_CONTAINER) {
                                    scale = Math.min(
                                        viewSize.width / imageSize.width,
                                        viewSize.height / imageSize.height
                                    );
                                } else {
                                    scale = 1.0;
                                }

                                scale = Math.min(1, scale);

                                return (
                                    <Positioner
                                        key="pos"
                                        imageSize={imageSize}
                                        viewSize={viewSize}
                                        scale={scale}
                                        render={({
                                            artboardSize,
                                            artboardOrigin,
                                            scaledImageSize,
                                            imageToScreen,
                                            screenToImage,
                                        }) => (
                                            <ScrollPane
                                                viewSize={viewSize}
                                                scaledImageSize={scaledImageSize}
                                            >
                                                <Artboard
                                                    width={artboardSize.width}
                                                    height={artboardSize.height}
                                                >
                                                    <Mobile>
                                                        {this.renderImage(
                                                            artboardOrigin,
                                                            imageSize,
                                                            scale
                                                        )}
                                                        {this.renderSketchLayer(
                                                            artboardOrigin,
                                                            artboardSize,
                                                            scale
                                                        )}
                                                    </Mobile>
                                                    <Desktop>
                                                        <ConnectedInputHandler
                                                            canAnnotate={
                                                                this.props.design.permission
                                                                    .canAnnotate
                                                            }
                                                            canSketch={
                                                                this.props.design.permission
                                                                    .canSketch
                                                            }
                                                            designId={this.props.design.designId}
                                                            feedbackMode={this.props.feedbackMode}
                                                            isPresentationMode={
                                                                this.props.isPresentationMode
                                                            }
                                                            toImageSpace={screenToImage}
                                                        >
                                                            {({
                                                                paths,
                                                                currentPath,
                                                                isEditing,
                                                                isHovering,
                                                            }) => [
                                                                this.renderImage(
                                                                    artboardOrigin,
                                                                    imageSize,
                                                                    scale
                                                                ),
                                                                this.renderSketchLayer(
                                                                    artboardOrigin,
                                                                    artboardSize,
                                                                    scale,
                                                                    currentPath
                                                                ),
                                                                isHovering &&
                                                                    !isEditing &&
                                                                    this.renderTooltip(),
                                                                <ConnectedAnnotationsCanvas
                                                                    key="a"
                                                                    design={this.props.design}
                                                                    isPresentationMode={
                                                                        this.props
                                                                            .isPresentationMode
                                                                    }
                                                                    feedbackMode={
                                                                        this.props.feedbackMode
                                                                    }
                                                                    toImageSpace={screenToImage}
                                                                    toScreenSpace={imageToScreen}
                                                                />,
                                                            ]}
                                                        </ConnectedInputHandler>
                                                    </Desktop>
                                                </Artboard>
                                            </ScrollPane>
                                        )}
                                    />
                                );
                            }}
                        />
                    </div>
                )}
            </MediaQuery>
        );
    }

    renderImage(artboardOrigin, imageSize, scale) {
        return (
            <img
                src={this.props.design.previewUrl}
                key={this.props.design.previewUrl}
                style={{
                    backgroundColor: '#f3f2f0', //TODO karl-the-fog
                    position: 'absolute',
                    left: artboardOrigin.x + 'px',
                    top: artboardOrigin.y + 'px',
                    width: imageSize.width * scale + 'px',
                    height: imageSize.height * scale + 'px',
                }}
            />
        );
    }

    renderSketchLayer(artboardOrigin, artboardSize, scale, currentPath) {
        if (this.props.isPresentationMode) return null;
        if (!this.props.design.permission.canViewSketches) return null;

        return (
            <SketchLayer
                key="sketch"
                artboardOrigin={artboardOrigin}
                artboardSize={artboardSize}
                scale={scale}
            >
                <ProvidePaths
                    designId={this.props.design.designId}
                    renderPath={(path, key) => <Path key={key} path={path} smooth />}
                />
                {currentPath && <Path key="current" path={currentPath} />}
            </SketchLayer>
        );
    }

    renderTooltip() {
        if (this.props.isPresentationMode || !this.props.design.permission.canAnnotate) return null;

        if (this.props.feedbackMode == 'sketch') {
            return (
                <AnnotationTooltip key="t">
                    <FormattedMessage id="click_to_draw" />
                </AnnotationTooltip>
            );
        }
        if (this.props.feedbackMode == 'annotate') {
            return (
                <AnnotationTooltip key="t">
                    <FormattedMessage id="click_to_comment" />
                </AnnotationTooltip>
            );
        }
    }
}
DesignPane.propTypes = {
    zoomLevel: PropTypes.oneOf([
        ZoomLevels.FLUID,
        ZoomLevels.FIT_TO_WIDTH,
        ZoomLevels.FIT_TO_CONTAINER,
        ZoomLevels.FIT_TO_NATIVE,
    ]).isRequired,
    onSize: PropTypes.func.isRequired,
    design: PropTypes.object.isRequired,
    isPresentationMode: PropTypes.bool.isRequired,
    feedbackMode: PropTypes.string.isRequired,
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/DesignPane/DesignPane.jsx